/* eslint-disable camelcase */
import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';

import { RIGHT_TO_LEFT_LOCALES } from '../../../utils/constants';
import {
  AustriaSpecificCompanyAddress,
  AustriaSpeceficHeader,
  CreditNote,
  DefectiveHeader,
  DigitalOrderNumber,
  DocumentDetails,
  EmployeeNumber,
  MexicoSpecificFooter,
  KoreaSpeceficHeader,
  FranceSpecificFooter,
  GermanySpecificFooter,
  GreeceSpecificContent,
  IndiaSpecificHeader,
  IsraelSpecificHeader,
  PolandSpecificHeader,
  PortugalSpecificFooter,
  PaymentInformation,
  PickupContactInformation,
  PortugalSpecificHeader,
  PriceOverride,
  PurchasedItems,
  ResumeTransactionHeader,
  ReturnedItems,
  SpainSpecificHeader,
  ShippingInformation,
  StoreHeader,
  SurveyCode,
  TaxExemptTitle,
  TaxRegistrationNumber,
  TransactionTimeAndPlace,
  TransactionTotals,
  TrainingMode,
  ThailandSpeceficHeader,
  TurkeySpecificHeader,
  GreeceSpecificFooter
} from '../../reusable/ej-view-sections';
import { ConsumerContact } from '../../reusable/ej-view-sections/ConsumerDetails';
import Footer from '../../reusable/ej-view-sections/Footer';
import GiftItem from '../../reusable/ej-view-sections/GiftItem';
import IndiaAuthSignature from '../../reusable/ej-view-sections/IndiaAuthSignature';

const { sortItems } = require('../../../utils/ej-utils');

// * Note: Only one of the array should be updated for adding a tax registration number.
const taxNumberCountries = ['AUS', 'MYS', 'SGP', 'THA', 'PRT', 'ZAF'];
const taxNumberLocales = ['he-IL', 'nn-NO', 'ca-ES', 'cs-CZ', 'da-DK', 'de-AT', 'de-CH', 'de-DE', 'en-GB', 'en-IE', 'en-NZ', 'es-ES', 'fr-BE', 'fr-CH', 'fr-FR', 'hr-HR', 'it-CH', 'nl-BE', 'nl-NL', 'pl-PL', 'pt-PT', 'sv-SE', 'tr-TR'];

const RetailTransactionView = ({
  locale, storeHeaderInfo, timezone, transaction, footers, index, isGiftReceipt, giftTransaction
}) => {
  const {
    RL_ABORTED, RL_DUPLICATERECEIPT, RL_TRANSACTION_SUSPENDED, RL_SUSPEND_TRAN, RL_VOIDTRANSACTION, RL_GIFTITEMRECEIPT
  } = useSelector((state) => state);
  const sortedItems = sortItems(transaction.RetailTransaction?.LineItems || transaction.OrderTransaction?.LineItems || []);

  const taxRegistrationCountry = taxNumberCountries.includes(transaction.NikeDigital?.Country) ? transaction.NikeDigital.Country : '';
  const taxRegistrationLocale = taxNumberLocales.includes(locale.localeName) ? locale.localeName : '';
  const digitalOrderID = transaction?.RetailTransaction?.DigitalOrderID || transaction.OrderTransaction?.DigitalOrderID;
  const greeceFiscalSignature = (transaction?.RetailTransaction?.GreeceFiscal?.FiscalSignature) ? (transaction?.RetailTransaction?.GreeceFiscal?.FiscalSignature).split('-')[0] : '';
  const storeCreditTenderInfo = {
    LocationCode: transaction.BusinessUnit?.LocationCode,
    Operator: transaction.Operator?.ID,
    SequenceNumber: transaction.SequenceNumber,
    TillID: transaction.TillID
  };
  const dsisOrder = 'Delivery' in sortedItems;
  const isRightToLeft = RIGHT_TO_LEFT_LOCALES.includes(locale.localeName) ? 'RTL' : 'LTR';
  const dorisOrder = (transaction.RetailTransaction?.TransactionLinks || []).filter((link) => link?.BusinessUnit?.LocationCode === 'DIGITAL').length;
  const isResumeTransaction = transaction.RetailTransaction?.TransactionLinks && transaction.RetailTransaction.TransactionLinks.filter((x) => x.ReasonCode === 'Resume').length;
  return (
    <section
      className="p2-sm body-4 flx-jc-sm-c display-print"
      style={{
        direction: isRightToLeft, margin: 'auto', maxWidth: 275, textAlign: 'center'
      }}
    >
      {['GBR', 'DEU', 'ESP'].includes(transaction.NikeDigital?.Country) && isResumeTransaction && (
      <ResumeTransactionHeader transactionNumber={transaction?.SequenceNumber} />
      )}
      <br />
      {transaction.NikeDigital?.Country === 'THA' && (
        <ThailandSpeceficHeader
          contactDetails={transaction?.RetailTransaction?.Customers || transaction.OrderTransaction?.Customers || []}
          returnedItems={sortedItems.Returns}
        />
  )}
      <StoreHeader
        address={storeHeaderInfo.address}
        digitalOrder={dsisOrder || dorisOrder}
        locale={locale}
        localizations={storeHeaderInfo.localizations}
        name={storeHeaderInfo.name}
        phoneNumber={storeHeaderInfo.phoneNumber}
        storeConcept={storeHeaderInfo.storeConcept}
        taxes={sortedItems.Tax}
        transaction={transaction}
      />
      <p>{`***${RL_DUPLICATERECEIPT}***`}</p>
      {<DefectiveHeader returnedItems={sortedItems?.Returns} />}
      {locale.localeName === 'el-GR' && (
        <>
          <p>АФМ: {greeceFiscalSignature}</p>
          <br />
        </>
      )}
      {locale.localeName === 'en-IN' && (
      <><IndiaSpecificHeader companyTaxID={transaction?.BusinessUnit?.CompanyTaxID} digitalOrderNumber={digitalOrderID} invoiceNumber={transaction?.InvoiceNumber || transaction?.InvoiceNumber} items={sortedItems} vatRegistrationNumber={transaction?.VATRegistrationNumber} />
      </>
      )}
      {locale.localeName !== 'en-IN' && (digitalOrderID && <DigitalOrderNumber digitalOrderNumber={digitalOrderID} />)}
      {locale.localeName === 'el-GR' && (
        <>
          <p>ΣΕΙΡΑ ΑΠΟΔΕΙΞΗΣ ΛΙΑΝΙΚΗΣ: {transaction?.RetailTransaction?.GreeceFiscal?.FiscalSequence}</p>
          <br />
        </>
      )}
      {locale.localeName === 'ko-KR' && <KoreaSpeceficHeader companyRegistrationNumber={transaction?.BusinessUnit?.CompanyRegistrationNumber} generalManagerName={transaction?.BusinessUnit?.GeneralManager?.FullName} />}
      {locale.localeName === 'pl-PL' && (
      <PolandSpecificHeader
        contactDetails={transaction?.RetailTransaction?.Customers || transaction.OrderTransaction?.Customers || []}
      />
      )}
      {locale.localeName === 'tr-TR' && (
      <TurkeySpecificHeader
        contactDetails={transaction?.RetailTransaction?.Customers || transaction.OrderTransaction?.Customers || []}
        invoiceNumber={transaction?.InvoiceNumber}
      />
      )}
      {(taxRegistrationCountry || taxRegistrationLocale)
          && (
          <TaxRegistrationNumber
            country={taxRegistrationCountry}
            invoiceNumber={['NOR', 'THA'].includes(transaction.NikeDigital?.Country) ? transaction?.RetailTransaction?.Customers[0]?.TaxInvoice?.Number : ''}
            locale={taxRegistrationLocale}
            posID={transaction?.Workstation?.SerialNumber}
            siretNumber={transaction?.BusinessUnit?.SiretNumber}
            taxes={sortedItems.Tax}
          />
          )}
      {locale.localeName === 'de-AT' && (<AustriaSpecificCompanyAddress />)}
      {locale.localeName === 'he-IL' && (
      <IsraelSpecificHeader
        contactDetails={transaction?.RetailTransaction?.Customers || transaction.OrderTransaction?.Customers || []}
        invoiceNumber={transaction?.InvoiceNumber || ''}
      />
      )}
      {(locale.localeName === 'es-ES' || locale.localeName === 'ca-ES') && (
      <SpainSpecificHeader
        contactDetails={transaction?.RetailTransaction?.Customers || transaction.OrderTransaction?.Customers || []}
        country={transaction?.NikeDigital?.Country}
      />
      )}
      {(locale.localeName === 'pt-PT' && transaction?.InvoiceNumber) && (
      <PortugalSpecificHeader
        contactDetails={transaction?.RetailTransaction?.Customers || transaction.OrderTransaction?.Customers || []}
        invoiceNumber={transaction?.InvoiceNumber}
        transaction={transaction}
      />
      )}
      {transaction.TrainingModeFlag && <TrainingMode />}

      {/* isGiftReceipt = true flag will omit below fields for gift item receipt */}
      {!isGiftReceipt && (
        <>
          {sortedItems.Returns?.length && transaction.NikeDigital?.Country === 'THA'
        && (
        <CreditNote
          contactDetails={transaction?.RetailTransaction?.Customers || transaction.OrderTransaction?.Customers || []}
          returnedItems={sortedItems.Returns}
        />
        )}
          {(sortedItems.Sales?.length || sortedItems.Returns?.length || sortedItems?.Delivery?.length || sortedItems?.Pickup?.length)
        && (transaction.OrderTransaction?.TransactionStatus !== 'Suspended' && transaction.OrderTransaction?.TransactionStatus !== 'Canceled') && (
        <TaxExemptTitle
          contactDetails={transaction?.RetailTransaction?.Customers || transaction.OrderTransaction?.Customers || []}
        />
          )}
          {(transaction.DiscountPass?.Employee && locale.localeName !== 'en-IN') && <EmployeeNumber EmployeeId={transaction?.DiscountPass?.Employee?.ID} />}
          <AustriaSpeceficHeader fiscalDocuments={transaction?.RetailTransaction?.FiscalDocuments} locale={locale.localeName} />
          {transaction.OrderTransaction?.TransactionStatus === 'Suspended' && <p>*****{RL_SUSPEND_TRAN}*****</p>}
          {transaction.OrderTransaction?.TransactionStatus === 'Canceled' && <p>*****{RL_VOIDTRANSACTION}*****</p>}
          {transaction.NikeDigital?.Country === 'CHL' && <DocumentDetails Details={transaction?.RetailTransaction?.FiscalDocuments} />}
          {sortedItems.Returns?.length && (
            <ReturnedItems
              contactDetails={transaction?.RetailTransaction?.Customers || transaction.OrderTransaction?.Customers || []}
              country={transaction.NikeDigital?.Country}
              locale={locale.localeName}
              returnedItems={sortedItems.Returns}
              sourcePOS={transaction.RetailTransaction?.SourcePOS?.Type || transaction.OrderTransaction?.SourcePOS?.Type}
            />
          )}
          {(sortedItems.Sales?.length || sortedItems.Delivery?.length || sortedItems.Pickup?.length) && (
          <PurchasedItems
            country={storeHeaderInfo.address.country}
            saleForDelivery={sortedItems?.Delivery
              ?.filter((item) => item?.SaleForDelivery?.ItemType !== 'Shipping')
              ?.map((item) => ({
                ...item.SaleForDelivery,
                saleOfGiftCard: item.SaleOfGiftCard,
                voidFlag: item.VoidFlag
              }))}
            saleForPickupItems={sortedItems?.Pickup?.map((item) => ({ ...item.SaleForPickup, saleOfGiftCard: item.SaleOfGiftCard, voidFlag: item.VoidFlag }))}
            saleItems={sortedItems?.Sales?.map((item) => ({ ...item.Sale, saleOfGiftCard: item.SaleOfGiftCard, voidFlag: item.VoidFlag }))}
            sourcePOS={transaction.RetailTransaction?.SourcePOS?.Type || transaction.OrderTransaction?.SourcePOS?.Type}
          />
          )}
          {sortedItems.Pickup?.length && <PickupContactInformation contactDetails={transaction?.RetailTransaction} />}
          {sortedItems.Delivery?.length && (
            <ShippingInformation
              deliveryItems={sortedItems?.Delivery}
              saleItems={[
                ...sortedItems?.Sales || [],
                ...sortedItems?.Delivery?.filter((item) => item?.SaleForDelivery?.ItemType === 'Shipping')
                  .map((item) => ({
                    ...item,
                  }))
              ]}
            />
      )}
          <br />
          {(sortedItems.Delivery?.filter((item) => item?.SaleForDelivery?.ItemType === 'Shipping')?.length
            || sortedItems.Sales?.filter((item) => item?.Sale?.ItemType === 'Shipping')?.length) && (
            <ConsumerContact customerDetails={transaction?.RetailTransaction?.Customers || transaction.OrderTransaction?.Customers || []} />
          )}
          <br />
          {(sortedItems.Sales?.length || sortedItems.Returns?.length || sortedItems?.Delivery?.length || sortedItems?.Pickup?.length)
        && (transaction.OrderTransaction?.TransactionStatus !== 'Suspended' && transaction.OrderTransaction?.TransactionStatus !== 'Canceled') && (
          <TransactionTotals
            contactDetails={transaction?.RetailTransaction?.Customers || transaction.OrderTransaction?.Customers || []}
            country={transaction.NikeDigital?.Country || storeHeaderInfo.address.country}
            customerDetails={transaction?.RetailTransaction?.Customers || []}
            purchasedItems={[...(sortedItems?.Delivery?.filter((item) => item?.SaleForDelivery?.ItemType !== 'Shipping')?.map((item) => ({ ...item.SaleForDelivery, voidFlag: item.VoidFlag })) || []),
              ...(sortedItems?.Pickup?.map((item) => ({ ...item.SaleForPickup, voidFlag: item.VoidFlag })) || []),
              ...(sortedItems?.Sales?.map((item) => ({ ...item.Sale, voidFlag: item.VoidFlag })) || [])]}
            returnedItems={sortedItems.Returns}
            taxes={sortedItems.Tax}
            totals={transaction.RetailTransaction?.Totals || transaction.OrderTransaction?.Totals || []}
          />
          )}
          {(locale.localeName === 'el-GR' && (transaction?.RetailTransaction?.Customers)) && ( // Greece specific customer details
            <GreeceSpecificContent customers={transaction?.RetailTransaction?.Customers} />
          )}
          {sortedItems.Payments?.length && <PaymentInformation paymentInformation={sortedItems.Payments} storeCreditTenderInfo={storeCreditTenderInfo} timezone={timezone} />}
          {transaction.OrderTransaction?.TransactionStatus === 'Suspended' && <p>*****{RL_TRANSACTION_SUSPENDED}*****</p>}
          {transaction.OrderTransaction?.TransactionStatus === 'Canceled' && <p>*****{RL_ABORTED}*****</p>}
        </>
      )}
      {isGiftReceipt && (
        <>
          <p>{`** ${RL_GIFTITEMRECEIPT} **`}</p>
          <GiftItem index={index} item={giftTransaction} />
        </>
      )}
      <TransactionTimeAndPlace
        address={storeHeaderInfo?.address}
        customerDetails={transaction?.RetailTransaction?.Customers || []}
        date={transaction.BusinessDayDate}
        locale={locale}
        operator={transaction.Operator?.ID}
        register={transaction.Workstation?.ID}
        store={transaction.BusinessUnit?.LocationCode}
        suspendedTransactionReference={transaction?.SuspendedTransactionReference}
        time={transaction.EndDateTime}
        timezone={timezone}
        transaction={transaction.SequenceNumber}
      />
      {!isGiftReceipt && sortedItems.Sales?.length && (
      <PriceOverride
        saleItems={sortedItems?.Sales.map((item) => item.Sale)}
      />
      )}
      <GermanySpecificFooter GermanyFiscalValues={transaction?.GermanyFiscalValues} locale={locale.localeName} />
      {(transaction.NikeDigital?.Country === 'FRA' || locale.localeName === 'fr-FR') && (
      <FranceSpecificFooter
        sourcePOS={transaction.RetailTransaction?.SourcePOS}
        tranId={transaction.InvoiceSeqNumber}
      />
        )}
      {transaction.NikeDigital?.Country === 'MEX' && (
        <MexicoSpecificFooter referenceNumber={transaction?.InvoiceReferenceNumber} />
        )}
      {transaction.NikeDigital?.Country === 'PRT' && (
        <PortugalSpecificFooter qrCode={transaction?.QRCode} />
        )}

      <IndiaAuthSignature locale={locale.localeName} />

      <GreeceSpecificFooter GreeceFiscalValues={transaction?.RetailTransaction?.GreeceFiscal} isGiftReceipt={isGiftReceipt} locale={locale.localeName} />
      {/* Don't print footer and surveyCode if gift receipt */}
      {!isGiftReceipt && (
      <>
        <Footer
          footers={footers}
          surveyCode={transaction.NikeDigital?.Country === 'MEX' ? transaction.RetailTransaction?.Survey?.SurveyCode || transaction.OrderTransaction?.Survey?.SurveyCode : ''}
        />
        {transaction.RetailTransaction?.Survey?.SurveyCode && <SurveyCode surveyCode={transaction.RetailTransaction?.Survey?.SurveyCode} />}
      </>
      )}
    </section>
  );
};

RetailTransactionView.defaultProps = {
  footers: [],
  giftTransaction: {},
  index: 0,
  isGiftReceipt: false,
  locale: {},
  storeHeaderInfo: {},
  timezone: '',
  transaction: {}
};

RetailTransactionView.propTypes = {
  footers: PropTypes.arrayOf(PropTypes.string),
  giftTransaction: PropTypes.shape(),
  index: PropTypes.number,
  isGiftReceipt: PropTypes.bool,
  locale: PropTypes.shape(),
  storeHeaderInfo: PropTypes.shape(),
  timezone: PropTypes.string,
  transaction: PropTypes.shape()
};

export default RetailTransactionView;
